import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { adminAPI } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Avatar, AvatarFallback } from '../../components/ui/avatar';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import { cn, formatDateTime, getStatusColor } from '../../lib/utils';
import { 
  Search, 
  MoreHorizontal, 
  UserCheck, 
  UserX, 
  Loader2, 
  Users, 
  Plus, 
  Shield, 
  ClipboardCheck,
  Trash2,
  AlertTriangle,
  CheckCircle,
} from 'lucide-react';
import { toast } from 'sonner';

export default function AdminUsersPage() {
  const [searchParams] = useSearchParams();
  const initialRole = searchParams.get('role') || 'inspector';
  
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [activeTab, setActiveTab] = useState(initialRole === 'regulator' ? 'regulators' : 'inspectors');
  const [statusFilter, setStatusFilter] = useState('all');
  
  // Add User Dialog State
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [addingUser, setAddingUser] = useState(false);
  const [newUser, setNewUser] = useState({
    email: '',
    password: '',
    full_name: '',
    phone: '',
    region: '',
    business_name: '',
    certificate_number: '',
    license_expiry: '',
    role: 'inspector'
  });

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      const response = await adminAPI.getAllUsers();
      setUsers(response.data);
    } catch (err) {
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (userId, newStatus) => {
    try {
      await adminAPI.updateUser(userId, { status: newStatus }, 'Admin status update');
      setUsers(users.map(u => u.id === userId ? { ...u, status: newStatus } : u));
      toast.success('User status updated');
    } catch (err) {
      toast.error('Failed to update user');
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to remove this user?')) return;
    
    try {
      await adminAPI.deleteUser(userId, 'Admin removed user');
      setUsers(users.filter(u => u.id !== userId));
      toast.success('User removed');
    } catch (err) {
      toast.error('Failed to remove user');
    }
  };

  const handleAddUser = async () => {
    if (!newUser.email || !newUser.password || !newUser.full_name) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    setAddingUser(true);
    try {
      const response = await adminAPI.createUser({
        ...newUser,
        role: activeTab === 'inspectors' ? 'inspector' : 'regulator'
      });
      setUsers([response.data, ...users]);
      setAddDialogOpen(false);
      setNewUser({
        email: '',
        password: '',
        full_name: '',
        phone: '',
        region: '',
        business_name: '',
        certificate_number: '',
        license_expiry: '',
        role: 'inspector'
      });
      toast.success(`${activeTab === 'inspectors' ? 'Inspector' : 'Regulator'} created successfully`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create user');
    } finally {
      setAddingUser(false);
    }
  };

  const getInitials = (name) => {
    if (!name) return 'U';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const getRoleBadge = (role) => {
    const colors = {
      admin: 'bg-purple-100 text-purple-700 border-purple-200',
      inspector: 'bg-teal-100 text-teal-700 border-teal-200',
      regulator: 'bg-blue-100 text-blue-700 border-blue-200',
    };
    return colors[role] || colors.inspector;
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'active':
        return <CheckCircle className="h-4 w-4 text-emerald-500" />;
      case 'suspended':
        return <UserX className="h-4 w-4 text-red-500" />;
      case 'pending_approval':
        return <AlertTriangle className="h-4 w-4 text-amber-500" />;
      default:
        return null;
    }
  };

  // Filter users by role and search
  const inspectors = users.filter(u => u.role === 'inspector');
  const regulators = users.filter(u => u.role === 'regulator');
  
  const filteredUsers = (activeTab === 'inspectors' ? inspectors : regulators).filter(user => {
    const matchesSearch = !search || 
      user.full_name?.toLowerCase().includes(search.toLowerCase()) ||
      user.email?.toLowerCase().includes(search.toLowerCase()) ||
      user.business_name?.toLowerCase().includes(search.toLowerCase());
    const matchesStatus = statusFilter === 'all' || user.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  // Summary counts
  const activeInspectors = inspectors.filter(u => u.status === 'active').length;
  const pendingInspectors = inspectors.filter(u => u.status === 'pending_approval').length;
  const activeRegulators = regulators.filter(u => u.status === 'active').length;
  const pendingRegulators = regulators.filter(u => u.status === 'pending_approval').length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="admin-users">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl md:text-3xl font-bold">User Management</h1>
          <p className="text-muted-foreground mt-1">
            Manage inspectors and regulators
          </p>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="border-l-4 border-l-teal-500">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-teal-100 flex items-center justify-center">
                <ClipboardCheck className="h-5 w-5 text-teal-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{inspectors.length}</p>
                <p className="text-xs text-muted-foreground">Total Inspectors</p>
              </div>
            </div>
            <div className="mt-2 flex gap-3 text-xs">
              <span className="text-emerald-600">{activeInspectors} active</span>
              {pendingInspectors > 0 && (
                <span className="text-amber-600">{pendingInspectors} pending</span>
              )}
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-l-4 border-l-blue-500">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-blue-100 flex items-center justify-center">
                <Shield className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{regulators.length}</p>
                <p className="text-xs text-muted-foreground">Total Regulators</p>
              </div>
            </div>
            <div className="mt-2 flex gap-3 text-xs">
              <span className="text-emerald-600">{activeRegulators} active</span>
              {pendingRegulators > 0 && (
                <span className="text-amber-600">{pendingRegulators} pending</span>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs for Inspectors / Regulators */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-4">
          <TabsList>
            <TabsTrigger value="inspectors" className="gap-2">
              <ClipboardCheck className="h-4 w-4" />
              Inspectors ({inspectors.length})
            </TabsTrigger>
            <TabsTrigger value="regulators" className="gap-2">
              <Shield className="h-4 w-4" />
              Regulators ({regulators.length})
            </TabsTrigger>
          </TabsList>
          
          {/* Add User Button */}
          <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
            <DialogTrigger asChild>
              <Button data-testid="add-user-btn">
                <Plus className="mr-2 h-4 w-4" />
                Add {activeTab === 'inspectors' ? 'Inspector' : 'Regulator'}
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle>
                  Add New {activeTab === 'inspectors' ? 'Inspector' : 'Regulator'}
                </DialogTitle>
                <DialogDescription>
                  Create a new {activeTab === 'inspectors' ? 'inspector' : 'regulator'} account. They will be automatically activated.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label htmlFor="email">Email *</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="user@example.com"
                    value={newUser.email}
                    onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="password">Password *</Label>
                  <Input
                    id="password"
                    type="password"
                    placeholder="Minimum 6 characters"
                    value={newUser.password}
                    onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="full_name">Full Name *</Label>
                  <Input
                    id="full_name"
                    placeholder="John Doe"
                    value={newUser.full_name}
                    onChange={(e) => setNewUser({ ...newUser, full_name: e.target.value })}
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="phone">Phone</Label>
                    <Input
                      id="phone"
                      placeholder="+255..."
                      value={newUser.phone}
                      onChange={(e) => setNewUser({ ...newUser, phone: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="region">Region</Label>
                    <Input
                      id="region"
                      placeholder="Dar es Salaam"
                      value={newUser.region}
                      onChange={(e) => setNewUser({ ...newUser, region: e.target.value })}
                    />
                  </div>
                </div>
                {activeTab === 'inspectors' && (
                  <>
                    <div className="space-y-2">
                      <Label htmlFor="business_name">Business Name</Label>
                      <Input
                        id="business_name"
                        placeholder="Company name"
                        value={newUser.business_name}
                        onChange={(e) => setNewUser({ ...newUser, business_name: e.target.value })}
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="certificate_number">Certificate #</Label>
                        <Input
                          id="certificate_number"
                          placeholder="CERT-..."
                          value={newUser.certificate_number}
                          onChange={(e) => setNewUser({ ...newUser, certificate_number: e.target.value })}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="license_expiry">License Expiry</Label>
                        <Input
                          id="license_expiry"
                          type="date"
                          value={newUser.license_expiry}
                          onChange={(e) => setNewUser({ ...newUser, license_expiry: e.target.value })}
                        />
                      </div>
                    </div>
                  </>
                )}
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setAddDialogOpen(false)}>
                  Cancel
                </Button>
                <Button onClick={handleAddUser} disabled={addingUser}>
                  {addingUser && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Create {activeTab === 'inspectors' ? 'Inspector' : 'Regulator'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        {/* Filters */}
        <Card className="mb-4">
          <CardContent className="p-4">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search by name, email, or business..."
                  className="pl-10"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  data-testid="search-users"
                />
              </div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-full md:w-40">
                  <SelectValue placeholder="All Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="pending_approval">Pending</SelectItem>
                  <SelectItem value="suspended">Suspended</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Users Table - Same for both tabs */}
        <TabsContent value="inspectors" className="mt-0">
          <UserTable 
            users={filteredUsers}
            onStatusChange={handleStatusChange}
            onDelete={handleDeleteUser}
            getInitials={getInitials}
            getRoleBadge={getRoleBadge}
            getStatusIcon={getStatusIcon}
            userType="inspector"
          />
        </TabsContent>
        
        <TabsContent value="regulators" className="mt-0">
          <UserTable 
            users={filteredUsers}
            onStatusChange={handleStatusChange}
            onDelete={handleDeleteUser}
            getInitials={getInitials}
            getRoleBadge={getRoleBadge}
            getStatusIcon={getStatusIcon}
            userType="regulator"
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}

// Reusable User Table Component
function UserTable({ users, onStatusChange, onDelete, getInitials, getRoleBadge, getStatusIcon, userType }) {
  if (users.length === 0) {
    return (
      <Card>
        <CardContent className="py-12">
          <div className="text-center">
            <Users className="h-12 w-12 mx-auto text-muted-foreground/30 mb-4" />
            <p className="text-muted-foreground">No {userType}s found</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent className="p-0">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>User</TableHead>
                <TableHead className="hidden md:table-cell">
                  {userType === 'inspector' ? 'Business' : 'Organization'}
                </TableHead>
                <TableHead className="hidden lg:table-cell">Region</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="hidden md:table-cell">Registered</TableHead>
                <TableHead className="w-[50px]"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {users.map((user) => (
                <TableRow key={user.id} className="table-row-hover">
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <Avatar className="h-9 w-9">
                        <AvatarFallback className={cn(
                          "text-sm",
                          userType === 'inspector' 
                            ? "bg-teal-100 text-teal-700" 
                            : "bg-blue-100 text-blue-700"
                        )}>
                          {getInitials(user.full_name)}
                        </AvatarFallback>
                      </Avatar>
                      <div>
                        <p className="font-medium">{user.full_name}</p>
                        <p className="text-sm text-muted-foreground">{user.email}</p>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell className="hidden md:table-cell">
                    <div>
                      <p className="text-sm">{user.business_name || '-'}</p>
                      {user.certificate_number && (
                        <p className="text-xs text-muted-foreground font-mono">
                          {user.certificate_number}
                        </p>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="hidden lg:table-cell">
                    {user.region || '-'}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      {getStatusIcon(user.status)}
                      <Badge variant="outline" className={cn("capitalize", getStatusColor(user.status))}>
                        {user.status?.replace('_', ' ') || 'N/A'}
                      </Badge>
                    </div>
                  </TableCell>
                  <TableCell className="hidden md:table-cell text-sm text-muted-foreground">
                    {formatDateTime(user.created_at)}
                  </TableCell>
                  <TableCell>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon">
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        {user.status === 'pending_approval' && (
                          <DropdownMenuItem onClick={() => onStatusChange(user.id, 'active')}>
                            <UserCheck className="mr-2 h-4 w-4 text-emerald-600" />
                            Approve
                          </DropdownMenuItem>
                        )}
                        {user.status !== 'active' && user.status !== 'pending_approval' && (
                          <DropdownMenuItem onClick={() => onStatusChange(user.id, 'active')}>
                            <UserCheck className="mr-2 h-4 w-4" />
                            Activate
                          </DropdownMenuItem>
                        )}
                        {user.status === 'active' && (
                          <DropdownMenuItem 
                            onClick={() => onStatusChange(user.id, 'suspended')}
                            className="text-amber-600"
                          >
                            <UserX className="mr-2 h-4 w-4" />
                            Suspend
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuSeparator />
                        <DropdownMenuItem 
                          onClick={() => onDelete(user.id)}
                          className="text-destructive"
                        >
                          <Trash2 className="mr-2 h-4 w-4" />
                          Remove
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
}
